import { useEffect, useState, useRef, useCallback } from "react";

import MetaMaskOnboarding from "@metamask/onboarding";
import { v4 as uuid } from "uuid";
import { ethers } from "ethers";

import { doPolygonAuth } from "../Services/ApiClient";
import { useAuth } from "../Contexts/useAuth";
import { useMetamaskWalletAccount, useMetamaskChainId } from "../Utils/hooks";
import { MetamaskWalletContext } from "../Contexts/useMetamaskWallet";
import config from "../config.json";

const target_network_name = config["Metamask"]["TargetNetwork"];
const target_network = config["Metamask"]["Networks"][target_network_name];
const msgTpl = config["PolygonAuthTpl"];

export function MetamaskWalletProvider({ children }) {
    const {
        account,
        isLoading: accountIsLoading,
        isValidating: accountIsRefreshing,
        refreshAccount,
    } = useMetamaskWalletAccount();

    const {
        chainId,
        isLoading: chainIdIsLoading,
        isValidating: chainIdIsRefreshing,
        refreshChainId,
    } = useMetamaskChainId();

    const { jwt, verifiedPolygonAccts, saveVerifedPolygonAcct } = useAuth();

    const [currentVerifiedPolygonPubKey, setCurrentVerifiedPolygonPubKey] =
        useState("");

    const verifiedPolygonAcctsRef = useRef(verifiedPolygonAccts);

    // Auto Metamask Auth
    useEffect(() => {
        if (
            accountIsLoading ||
            accountIsRefreshing ||
            chainIdIsLoading ||
            chainIdIsRefreshing
        ) {
            return;
        }
        console.log("Current Acct:", account);
        if (chainId !== target_network["ChainId"]) {
            setCurrentVerifiedPolygonPubKey("");
            return;
        }
        if (
            account &&
            verifiedPolygonAcctsRef.current.length > 0 &&
            verifiedPolygonAcctsRef.current.indexOf(account) !== -1
        ) {
            setCurrentVerifiedPolygonPubKey(account);
        } else {
            setCurrentVerifiedPolygonPubKey("");
        }
    }, [
        account,
        chainId,
        accountIsRefreshing,
        chainIdIsRefreshing,
        accountIsLoading,
        chainIdIsLoading,
    ]);

    const onboarding = useRef(null);

    useEffect(() => {
        if (!onboarding.current) {
            onboarding.current = new MetaMaskOnboarding();
        }
    }, []);

    useEffect(() => {
        if (MetaMaskOnboarding.isMetaMaskInstalled()) {
            if (account && onboarding.current) {
                onboarding.current.stopOnboarding();
            }
        }
    }, [account]);

    useEffect(() => {
        const handleAcctChange = () => {
            setCurrentVerifiedPolygonPubKey("");
            refreshAccount();
        };

        const handleChainChange = (chainId) => {
            console.log("Chain Changed:", chainId);
            if (!chainId || chainId !== target_network["ChainId"]) {
                setCurrentVerifiedPolygonPubKey("");
                refreshChainId();
            }
        };
        if (MetaMaskOnboarding.isMetaMaskInstalled()) {
            window.ethereum.on("accountsChanged", handleAcctChange);
            window.ethereum.on("chainChanged", handleChainChange);
        }

        return () => {
            if (MetaMaskOnboarding.isMetaMaskInstalled()) {
                window.ethereum.removeListener(
                    "accountsChanged",
                    handleAcctChange
                );
                window.ethereum.removeListener(
                    "chainChanged",
                    handleChainChange
                );
            }
        };
    }, [refreshAccount, refreshChainId]);

    const doAuth = useCallback(async () => {
        let toBeVerifiedAcct;
        if (!account) {
            // request connect
            const accounts = await window.ethereum.request({
                method: "eth_requestAccounts",
            });
            toBeVerifiedAcct = ethers.getAddress(accounts[0]);
        } else {
            toBeVerifiedAcct = account;
        }
        console.log("Metamask Do Auth:", toBeVerifiedAcct);
        console.log("Metamask Do Auth:", chainId);
        if (chainId !== target_network["ChainId"]) {
            try {
                // request switch network
                await window.ethereum.request({
                    method: "wallet_switchEthereumChain",
                    params: [{ chainId: target_network["ChainId"] }],
                });
            } catch (error) {
                if (error.code === 4902) {
                    // request add network
                    await window.ethereum.request({
                        method: "wallet_addEthereumChain",
                        params: [
                            {
                                chainId: target_network["ChainId"],
                                chainName: target_network["ChainName"],
                                nativeCurrency:
                                    target_network["NativeCurrency"],
                                rpcUrls: target_network["RpcUrls"],
                                blockExplorerUrls:
                                    target_network["BlockExplorerUrls"],
                            },
                        ],
                    });
                    await window.ethereum.request({
                        method: "wallet_switchEthereumChain",
                        params: [{ chainId: target_network["ChainId"] }],
                    });
                } else {
                    throw error;
                }
            }
        }
        if (
            !verifiedPolygonAcctsRef.current ||
            verifiedPolygonAcctsRef.current.indexOf(toBeVerifiedAcct) === -1
        ) {
            // Sign Message
            const messageId = uuid();
            const msg = `0x${Buffer.from(
                msgTpl.replace("{{MESSAGE_ID}}", messageId),
                "utf8"
            ).toString("hex")}`;
            const signature = await window.ethereum.request({
                method: "personal_sign",
                params: [msg, toBeVerifiedAcct, ""],
            });
            // Verify Signature
            await doPolygonAuth(jwt, toBeVerifiedAcct, signature, messageId);

            if (saveVerifedPolygonAcct(toBeVerifiedAcct)) {
                verifiedPolygonAcctsRef.current.push(toBeVerifiedAcct);
            }
        }

        setCurrentVerifiedPolygonPubKey(toBeVerifiedAcct);
    }, [account, chainId, jwt, saveVerifedPolygonAcct]);

    const doMetamaskOnboarding = useCallback(() => {
        if (onboarding.current) {
            onboarding.current.startOnboarding();
        }
    }, []);

    return (
        <MetamaskWalletContext.Provider
            value={{
                currentVerifiedPolygonPubKey,
                isMetamaskInstalled: MetaMaskOnboarding.isMetaMaskInstalled(),
                retrievingAccount: accountIsLoading || accountIsRefreshing,
                retrievingChainId: chainIdIsLoading || chainIdIsRefreshing,
                doAuth,
                doMetamaskOnboarding,
            }}
        >
            {children}
        </MetamaskWalletContext.Provider>
    );
}
